import { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Grid, Box, FormLabel, FormGroup, FormControl, FilledInput, TextField, Icon, IconButton, Tooltip } from '@material-ui/core';
import { Link } from 'react-router-dom';
import { DataGrid } from '@material-ui/data-grid';
import Autocomplete from '@material-ui/lab/Autocomplete';

import Loader from '../../common/loader';
import Crud from '../../common/crud';
import Trash from '../../common/trash';

import AuthContext from '../../context';
import helpers from '../../utils/helpers';
import { get, post, put, del } from '../../utils/api-services';

const useStyles = makeStyles(() => ({
    root: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
    },

    tableContainer: {
        width: '100%'
    }
}));

const defRole = { id: 0, name: 'Choose a role' };

const UserIndex = ({ permissions }) => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const classes = useStyles();
    const [state, setState] = useState({
        loading: true,
        roles: [defRole],
        form: {
            iduser: 0,
            username: '',
            names: '',
            surnames: '',
            email: '',
            role: defRole
        },
        data: [],
        permissions: {}
    });

    useEffect(() => {
        setState(prev => ({ ...prev, permissions }));
    }, [permissions]);

    useEffect(() => {
        window.document.title = 'Users | IAM';

        makeRequest();
    }, []);

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/iamUser/index');
        if (response.status === 200) {
            setState(prev => ({
                ...prev,
                loading: false,
                data: response?.data ?? [],
                roles: response?.roles
            }));
        } else {
            console.log('[UserIndex] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const handleChange = ({ target }) => setState(prev => ({ ...prev, form: { ...prev.form, [target.name]: target.value } }));

    const handleAutocomplete = value => setState(prev => ({ ...prev, form: { ...prev.form, role: value } }));

    const validateForm = (type) => {
        const { iduser, username, names, surnames, email, role } = state.form;
        const { create_attr, update_attr, delete_attr } = state.permissions;

        switch (type) {
            case 'update':
                if (!update_attr) {
                    showToast('You have no permissions for this action', 'error', 6500);
                    return false;
                }
                const user = state.data.find(value => value.iduser === iduser);
                if (!user) {
                    showToast('Select some user', 'error');
                    return false;
                }
                const before = JSON.stringify(user);
                const after = JSON.stringify(state.form);
                if (before === after) {
                    showToast('Update some field', 'error');
                    return false;
                }
                if (!username.trim() || !names.trim() || !surnames.trim() || !email.trim() || Number(role.id) <= 0) {
                    showToast('All fields are required', 'error');
                    return false;
                }

                return true;
            case 'store':
                if (!create_attr) {
                    showToast('You have no permissions for this action', 'error', 6500);
                    return false;
                }
                if (!username.trim() || !names.trim() || !surnames.trim() || !email.trim() || Number(role.id) <= 0) {
                    showToast('All fields are required', 'error');
                    return false;
                }

                return true;
            case 'delete':
                if (!delete_attr) {
                    showToast('You have no permissions for this action', 'error', 6500);
                    return false;
                }
                if (!iduser) {
                    showToast('Select some user', 'error');
                    return false;
                }

                return window.confirm('Are you sure you want to delete this user?');
            default:
                break;
        }
    }

    const store = async () => {
        const cond = validateForm('store');

        if (cond) {
            showLoader(true);
            const response = await post('/iamUser/store', state.form);
            if (response.status === 200) {
                setState(prev => ({ ...prev, data: response?.data ?? [] }));
                showToast(response.message);
            } else {
                console.log('[UserIndex] store error', response);
                helpers.failRequest(response, showToast, signOut);
                showLoader(false);

                return false;
            }
            showLoader(false);
        }

        return cond;
    }

    const update = async () => {
        const { iduser } = state.form;
        const cond = validateForm('update');

        if (cond) {
            showLoader(true);
            const response = await put(`/iamUser/${iduser}/update`, state.form);
            if (response.status === 200) {
                setState(prev => ({ ...prev, data: response?.data ?? [] }));
                showToast(response.message);
            } else {
                console.log('[UserIndex] update error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const destroy = async () => {
        const { iduser } = state.form;
        const cond = validateForm('delete');

        if (cond) {
            showLoader(true);
            const response = await del(`/iamUser/${iduser}`);
            if (response.status === 200) {
                setState(prev => ({ ...prev, data: response?.data ?? [] }));
                showToast(response.message);
            } else {
                console.log('[UserIndex] destroy error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const clear = () => {
        setState(prev => ({
            ...prev,
            form: {
                ...prev.form,
                iduser: 0,
                username: '',
                names: '',
                surnames: '',
                email: '',
                role: defRole
            }
        }));
    }

    const valueGetter = ({ value }) => value?.name ?? 'N/A';

    const renderCell = ({ row }) => {
        return (
            <>
                <Link to={`/iam/users/detail/${row.iduser}`}>
                    <Tooltip title='Show detail' placement='right'>
                        <IconButton>
                            <Icon>{'visibility'}</Icon>
                        </IconButton>
                    </Tooltip>
                </Link>
                <Link to={`/history/users/${row.iduser}`} target='_blank'>
                    <Tooltip title='Show history' placement='top'>
                        <IconButton>
                            <Icon>{'history'}</Icon>
                        </IconButton>
                    </Tooltip>
                </Link>
            </>
        );
    }

    const columns = [
        { field: 'username', headerName: 'Username', flex: 1, minWidth: 200 },
        { field: 'names', headerName: 'Names', flex: 1, minWidth: 200 },
        { field: 'surnames', headerName: 'Surnames', flex: 1, minWidth: 200 },
        { field: 'email', headerName: 'Email', flex: 1, minWidth: 200 },
        { field: 'role', headerName: 'Role', flex: 1, minWidth: 200, valueGetter },
        { field: 'detail', headerName: 'Detail', flex: 0.5, minWidth: 100, renderCell }
    ];

    if (state.loading) return <Loader />;

    return (
        <div className={classes.root}>
            <Grid container>
                <Grid item xs={12} md={6} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Username'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                autoComplete='off'
                                type='text'
                                placeholder='Username'
                                name='username'
                                value={state.form.username}
                                onChange={handleChange}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Names'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                autoComplete='off'
                                type='text'
                                placeholder='Names'
                                name='names'
                                value={state.form.names}
                                onChange={handleChange}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Surnames'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                autoComplete='off'
                                type='text'
                                placeholder='Surnames'
                                name='surnames'
                                value={state.form.surnames}
                                onChange={handleChange}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Email'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                autoComplete='off'
                                type='text'
                                placeholder='Email'
                                name='email'
                                value={state.form.email}
                                onChange={handleChange}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Role'}</FormLabel>
                        <Autocomplete
                            options={state.roles}
                            value={state.form.role}
                            getOptionLabel={option => option?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(e, value) => handleAutocomplete(value)}
                            renderInput={params => <TextField {...params} placeholder='Role' />}
                        />
                    </FormGroup>
                </Grid>
            </Grid>

            <Crud
                permissions={state.permissions}
                create={{ create: clear, store }}
                update={update}
                destroy={destroy}
                cancel={clear}
            />

            {
                state.permissions.delete_attr &&
                <Trash table='users' id='iduser' onRefresh={makeRequest} />
            }

            <div className={classes.tableContainer}>
                <DataGrid
                    rows={state.data}
                    columns={columns}
                    pageSize={45}
                    density='compact'
                    autoHeight
                    getRowId={row => row.iduser}
                    onRowClick={({ row }) => setState(prev => ({ ...prev, form: row }))}
                />
            </div>
        </div>
    );
}

export default UserIndex;